import { useMemo } from "react"
import { IFactorsViewContextValue } from "./types"

/**
 * Custom hook to extract FactorsView-specific analytics properties with memoization.
 * Only recomputes when primitive analytics values change.
 *
 * Note: Dashboard properties (modelId, instrumentId, selectedMode, etc.) are inherited
 * automatically through provider nesting - they should NOT be extracted here.
 *
 * @param factorsViewContext - The factors view context value
 * @returns Memoized factors view analytics properties
 */
export function useExtractFactorsViewContextProperties(
  factorsViewContext: IFactorsViewContextValue,
) {
  const view = factorsViewContext.view
  const selectedMetricDisplay = factorsViewContext.selectedMetricDisplay
  const selectedPeriodPerformance = factorsViewContext.selectedPeriodPerformance
  const groupById = factorsViewContext.groupBy?.id
  const groupByLabel = factorsViewContext.groupBy?.label
  const sortMetricId = factorsViewContext.sortMetricId
  const sortDirection = factorsViewContext.sortDirection
  const navigationPath = factorsViewContext.selectedTableLevels?.join("-")
  const navigationDepth = factorsViewContext.selectedTableLevels?.length

  // Return memoized object depending only on primitive values
  return useMemo(
    () => ({
      view,
      selectedMetricDisplay,
      selectedPeriodPerformance,
      groupBy: groupById || groupByLabel,
      sortMetricId: sortMetricId || undefined,
      sortDirection,
      navigationPath,
      navigationDepth,
    }),
    [
      view,
      selectedMetricDisplay,
      selectedPeriodPerformance,
      groupById,
      groupByLabel,
      sortMetricId,
      sortDirection,
      navigationPath,
      navigationDepth,
    ],
  )
}
